import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const HardyWeinbergCalculator = () => {
  const [populationData, setPopulationData] = useState({
    dominantHomozygous: '',
    heterozygous: '',
    recessiveHomozygous: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPopulationData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateFrequencies = () => {
    const { dominantHomozygous, heterozygous, recessiveHomozygous } = populationData;
    
    // Convert inputs to numbers
    const AA = parseFloat(dominantHomozygous) || 0;
    const Aa = parseFloat(heterozygous) || 0;
    const aa = parseFloat(recessiveHomozygous) || 0;
    
    // Calculate total population
    const totalPopulation = AA + Aa + aa;
    
    if (totalPopulation === 0) {
      setError('Please enter valid population numbers');
      return;
    }

    // Calculate allele frequencies
    const pFreq = (2 * AA + Aa) / (2 * totalPopulation);
    const qFreq = (2 * aa + Aa) / (2 * totalPopulation);

    // Calculate expected genotype frequencies
    const expectedAA = Math.pow(pFreq, 2) * totalPopulation;
    const expectedAa = 2 * pFreq * qFreq * totalPopulation;
    const expectedaa = Math.pow(qFreq, 2) * totalPopulation;

    const calculationSteps = [
      'Step 1: Calculate total population',
      `Total population = AA + Aa + aa = ${AA} + ${Aa} + ${aa} = ${totalPopulation}`,
      '\nStep 2: Calculate allele frequencies',
      `p = (2×AA + Aa) / (2×Total) = (2×${AA} + ${Aa}) / (2×${totalPopulation}) = ${pFreq.toFixed(4)}`,
      `q = (2×aa + Aa) / (2×Total) = (2×${aa} + ${Aa}) / (2×${totalPopulation}) = ${qFreq.toFixed(4)}`,
      '\nStep 3: Calculate expected genotype frequencies',
      `AA (p²) = ${pFreq.toFixed(4)}² × ${totalPopulation} = ${expectedAA.toFixed(2)}`,
      `Aa (2pq) = 2 × ${pFreq.toFixed(4)} × ${qFreq.toFixed(4)} × ${totalPopulation} = ${expectedAa.toFixed(2)}`,
      `aa (q²) = ${qFreq.toFixed(4)}² × ${totalPopulation} = ${expectedaa.toFixed(2)}`
    ];

    const newResults = {
      alleleFrequencies: {
        p: pFreq.toFixed(4),
        q: qFreq.toFixed(4)
      },
      observedGenotypes: {
        AA: AA,
        Aa: Aa,
        aa: aa
      },
      expectedGenotypes: {
        AA: expectedAA.toFixed(2),
        Aa: expectedAa.toFixed(2),
        aa: expectedaa.toFixed(2)
      },
      totalPopulation,
      calculationSteps
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      bar: {
        labels: ['AA', 'Aa', 'aa'],
        datasets: [
          {
            label: 'Observed Genotypes',
            data: [AA, Aa, aa],
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          },
          {
            label: 'Expected Genotypes',
            data: [expectedAA, expectedAa, expectedaa],
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1
          }
        ]
      },
      pie: {
        labels: ['Allele A (p)', 'Allele a (q)'],
        datasets: [{
          data: [pFreq * 2 * totalPopulation, qFreq * 2 * totalPopulation],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)'
          ],
          borderWidth: 1
        }]
      }
    });
  };

  const exampleData = {
    AA: 25,
    Aa: 50,
    aa: 25,
    total: 100,
    pFreq: 0.5,
    qFreq: 0.5,
    expectedAA: 25,
    expectedAa: 50,
    expectedaa: 25
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🧬</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Hardy-Weinberg Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Genetic Equilibrium Analysis</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          The Hardy-Weinberg Calculator helps determine if a population is in genetic equilibrium by analyzing allele and genotype frequencies. This principle assumes no mutation, migration, selection, or genetic drift.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="dominantHomozygous">
              AA (Dominant Homozygous)
            </label>
            <input
              type="number"
              id="dominantHomozygous"
              name="dominantHomozygous"
              value={populationData.dominantHomozygous}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="heterozygous">
              Aa (Heterozygous)
            </label>
            <input
              type="number"
              id="heterozygous"
              name="heterozygous"
              value={populationData.heterozygous}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="recessiveHomozygous">
              aa (Recessive Homozygous)
            </label>
            <input
              type="number"
              id="recessiveHomozygous"
              name="recessiveHomozygous"
              value={populationData.recessiveHomozygous}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <button
            onClick={calculateFrequencies}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Calculate Frequencies
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}
        </div>

        {results && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold mb-4">Allele Frequencies</h3>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>p (A)</span>
                    <span className="font-medium">{results.alleleFrequencies.p}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>q (a)</span>
                    <span className="font-medium">{results.alleleFrequencies.q}</span>
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold mb-4">Total Population</h3>
                <p className="text-xl font-medium">{results.totalPopulation} individuals</p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold mb-4">Equilibrium Check</h3>
                <p className="text-sm text-gray-600">
                  {results.equilibrium ? 'Population is in Hardy-Weinberg equilibrium' : 'Population is NOT in Hardy-Weinberg equilibrium'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
              <div>
                <h3 className="text-xl font-semibold mb-4">Genotype Distribution</h3>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Number of Individuals'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Genotype'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Observed vs Expected Genotype Distribution'
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4">Allele Frequency Pie Chart</h3>
                <div className="h-96">
                  <Pie
                    data={chartData.pie}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Allele Frequency Distribution'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Calculation Steps</h3>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation using a sample population:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Population Data:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>AA (Dominant Homozygous): {exampleData.AA} individuals</li>
              <li>Aa (Heterozygous): {exampleData.Aa} individuals</li>
              <li>aa (Recessive Homozygous): {exampleData.aa} individuals</li>
              <li>Total Population: {exampleData.total} individuals</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Allele Frequencies:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>p = (2×AA + Aa) / (2×Total) = (2×25 + 50) / (2×100) = {exampleData.pFreq}</li>
                  <li>q = (2×aa + Aa) / (2×Total) = (2×25 + 50) / (2×100) = {exampleData.qFreq}</li>
                </ul>
              </li>
              <li>
                <p>Expected Genotype Numbers:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>AA = p² × Total = 0.5² × 100 = {exampleData.expectedAA}</li>
                  <li>Aa = 2pq × Total = 2(0.5)(0.5) × 100 = {exampleData.expectedAa}</li>
                  <li>aa = q² × Total = 0.5² × 100 = {exampleData.expectedaa}</li>
                </ul>
              </li>
            </ol>
            
            <p className="mt-6 font-semibold">Interpretation:</p>
            <p>In this example, the observed numbers match the expected numbers exactly, indicating the population is in Hardy-Weinberg equilibrium.</p>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Hardy-Weinberg calculator:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Population Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the number of AA (dominant homozygous) individuals</li>
                <li>Input the number of Aa (heterozygous) individuals</li>
                <li>Input the number of aa (recessive homozygous) individuals</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Click Calculate</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>The calculator will determine allele frequencies</li>
                <li>Expected genotype numbers will be calculated</li>
                <li>A visual comparison of observed vs. expected frequencies will be displayed</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Interpret Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Compare observed and expected numbers</li>
                <li>Check if p + q = 1</li>
                <li>Review the calculation steps for detailed understanding</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Pro Tips</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Use whole numbers for population counts</li>
                <li>Ensure all three genotype fields are filled</li>
                <li>The larger the population size, the more reliable the results</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding the Results <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            The Hardy-Weinberg calculator provides several key pieces of information:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Allele Frequencies:</span> 'p' represents the frequency of the dominant allele (A), while 'q' represents the frequency of the recessive allele (a). In equilibrium, p + q = 1.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Observed Numbers:</span> The actual count of individuals with each genotype in your population.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Expected Numbers:</span> The theoretical number of individuals with each genotype if the population is in Hardy-Weinberg equilibrium.
            </li>
          </ul>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Interpreting Hardy-Weinberg Equilibrium <span className="ml-2 text-2xl">🔍</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            A population is considered to be in Hardy-Weinberg equilibrium when:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Observed matches expected:</span> The observed genotype frequencies closely match the expected frequencies.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Allele frequency sum:</span> p + q = 1
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Genotype frequency sum:</span> p² + 2pq + q² = 1
            </li>
          </ul>
          <p className="mt-6 text-gray-600">
            Deviations from these expectations might indicate that one or more assumptions of the Hardy-Weinberg equilibrium are being violated (e.g., non-random mating, selection, mutation, gene flow, or genetic drift).
          </p>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Additional Tips <span className="ml-2 text-2xl">💡</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Data Quality:</span> Enter whole numbers for population counts and ensure all individuals are accounted for.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Population Size:</span> Larger populations generally provide more reliable results than smaller samples.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Multiple Tests:</span> Consider testing multiple populations or generations to observe changes over time.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Limitations:</span> Remember that Hardy-Weinberg equilibrium is a theoretical model. Real populations rarely meet all the assumptions perfectly.
            </li>
          </ul>
          <p className="mt-6 italic text-gray-500">
            Note: Small populations may show deviations from Hardy-Weinberg expectations due to random chance (genetic drift).
          </p>
        </div>
      </div>
    </div>
  );
};

export default HardyWeinbergCalculator;